import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { HAlignType, Styles } from "jspdf-autotable";
import { resolveUsuarioActual } from "../ControlStock/formatters";

export type VentasComparativoExportRow = {
  sucursal: string;
  subdivision: string | null;
  ventaAnterior: number;
  ventaActual: number;
  variacionAbs: number;
  variacionPct: number | null;
  ticketsAnterior: number;
  ticketsActual: number;
  ticketsVariacionAbs: number;
};

type VentasComparativoTotals = {
  ventaAnterior: number;
  ventaActual: number;
  variacionAbs: number;
  ticketsAnterior: number;
  ticketsActual: number;
  ticketsVariacionAbs: number;
};

export type VentasComparativoExportConfig = {
  tab: "mes" | "dia" | "semana";
  rows: VentasComparativoExportRow[];
  showSubdivision: boolean;
  vistaLabel: string;
  periodoLabel: string;
  rangoAnterior: string;
  rangoActual: string;
  corteLabel: string | null;
  totalLabel: string;
  total: VentasComparativoTotals;
  totalVariacionPct: number | null;
  server: string;
  anhoAnterior: number;
  anhoActual: number;
  syncDateTimeLabel?: string | null;
  logoDataUrl?: string | null;
};

const moneyFormat = new Intl.NumberFormat("es-PY", {
  maximumFractionDigits: 0,
});

const numberFormat = new Intl.NumberFormat("es-PY", {
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => moneyFormat.format(value ?? 0);
const formatNumber = (value: number) => numberFormat.format(value ?? 0);
const formatVariationPct = (value: number | null) =>
  value === null || value === undefined ? "-" : `${value.toFixed(1)}%`;

const formatSubdivision = (value?: string | null) => {
  const cleaned = String(value ?? "").trim();
  return cleaned.length > 0 ? cleaned : "—";
};

const tabSuffixMap: Record<VentasComparativoExportConfig["tab"], string> = {
  mes: "mensual",
  dia: "diario",
  semana: "semanal",
};

const buildTimestamp = () =>
  new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

const TOTAL_ROW_CELL_STYLE = {
  fontStyle: "bold" as const,
  fillColor: [0, 0, 0] as [number, number, number],
  textColor: [255, 255, 255] as [number, number, number],
};

const excelColumnHeader = (
  base: string,
  anho: number,
  rango: string,
) => `${base} ${anho} (${rango})`;

const appendExcelSheet = (
  workbook: XLSX.WorkBook,
  config: VentasComparativoExportConfig,
  sheetName: string,
) => {
  if (config.rows.length === 0) {
    return;
  }

  const rows = config.rows.map((row) => {
    const exportRow: Record<string, string | number> = {
      Sucursal: row.sucursal,
    };
    if (config.showSubdivision) {
      exportRow.Subdivision = formatSubdivision(row.subdivision);
    }
    exportRow[excelColumnHeader("Ventas", config.anhoAnterior, config.rangoAnterior)] =
      row.ventaAnterior;
    exportRow[excelColumnHeader("Ventas", config.anhoActual, config.rangoActual)] =
      row.ventaActual;
    exportRow["Diferencia (Gs)"] = row.variacionAbs;
    exportRow["% Diferencia"] = formatVariationPct(row.variacionPct);
    exportRow[excelColumnHeader("Tickets", config.anhoAnterior, config.rangoAnterior)] =
      row.ticketsAnterior;
    exportRow[excelColumnHeader("Tickets", config.anhoActual, config.rangoActual)] =
      row.ticketsActual;
    exportRow["Dif. Tickets"] = row.ticketsVariacionAbs;
    return exportRow;
  });

  const totalRow: Record<string, string | number> = {
    Sucursal: config.totalLabel,
  };
  if (config.showSubdivision) {
    totalRow.Subdivision = "-";
  }
  totalRow[excelColumnHeader("Ventas", config.anhoAnterior, config.rangoAnterior)] =
    config.total.ventaAnterior;
  totalRow[excelColumnHeader("Ventas", config.anhoActual, config.rangoActual)] =
    config.total.ventaActual;
  totalRow["Diferencia (Gs)"] = config.total.variacionAbs;
  totalRow["% Diferencia"] = formatVariationPct(config.totalVariacionPct);
  totalRow[excelColumnHeader("Tickets", config.anhoAnterior, config.rangoAnterior)] =
    config.total.ticketsAnterior;
  totalRow[excelColumnHeader("Tickets", config.anhoActual, config.rangoActual)] =
    config.total.ticketsActual;
  totalRow["Dif. Tickets"] = config.total.ticketsVariacionAbs;

  const worksheet = XLSX.utils.json_to_sheet([...rows, totalRow]);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
};

export const exportVentasComparativoExcel = (
  config: VentasComparativoExportConfig,
) => {
  if (config.rows.length === 0) {
    return;
  }

  const workbook = XLSX.utils.book_new();
  appendExcelSheet(workbook, config, "Comparativo");
  XLSX.writeFile(
    workbook,
    `comparativo-ventas-${tabSuffixMap[config.tab]}-${buildTimestamp()}.xlsx`,
  );
};

export const exportVentasComparativoExcelBatch = (
  configs: VentasComparativoExportConfig[],
) => {
  const validConfigs = configs.filter((config) => config.rows.length > 0);
  if (validConfigs.length === 0) {
    return;
  }
  const workbook = XLSX.utils.book_new();
  const sheetCountByName = new Map<string, number>();
  validConfigs.forEach((config) => {
    const baseName = config.vistaLabel || "Comparativo";
    const currentCount = sheetCountByName.get(baseName) ?? 0;
    sheetCountByName.set(baseName, currentCount + 1);
    const sheetName =
      currentCount === 0 ? baseName : `${baseName.slice(0, 25)}-${currentCount + 1}`;
    appendExcelSheet(workbook, config, sheetName);
  });
  XLSX.writeFile(workbook, `comparativo-ventas-todas-${buildTimestamp()}.xlsx`);
};

export const exportVentasComparativoPdf = (
  config: VentasComparativoExportConfig,
) => {
  if (config.rows.length === 0) {
    return;
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });
  const generatedAt = new Date().toLocaleString("es-PY");
  const userName = resolveUsuarioActual();

  const leftX = 40;
  const rightX = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const infoRightX = pageWidth - rightX;
  const infoStartY = 28;
  const infoRowHeight = 11;
  const infoMaxValueWidth = 190;
  const labelColor: [number, number, number] = [0, 0, 0];
  const valueColor: [number, number, number] = [120, 120, 120];

  const logoWidth = 110;
  const logoHeight = 24;
  const titleX = leftX;
  const titleY = config.logoDataUrl ? 54 : 36;
  const titleBottomY = titleY + 35;

  const infoLines: Array<{ label: string; value: string }> = [
    { label: "Vista", value: config.vistaLabel },
    { label: "Periodo", value: config.periodoLabel },
    { label: "Rango anterior", value: config.rangoAnterior },
    { label: "Rango actual", value: config.rangoActual },
  ];
  if (config.corteLabel) {
    infoLines.push({ label: "Corte", value: config.corteLabel });
  }
  if (config.syncDateTimeLabel) {
    infoLines.push({
      label: "Últ. sincronización",
      value: config.syncDateTimeLabel,
    });
  }

  const infoBottomY = infoStartY + (infoLines.length - 1) * infoRowHeight + 2;
  const headerContentBottom = Math.max(titleBottomY, infoBottomY);
  const headerHeight = headerContentBottom + 12;

  const fitTextToWidth = (text: string, maxWidth: number) => {
    if (maxWidth <= 0) return "";
    const raw = String(text ?? "");
    if (doc.getTextWidth(raw) <= maxWidth) {
      return raw;
    }
    let value = raw;
    while (value.length > 0 && doc.getTextWidth(`${value}...`) > maxWidth) {
      value = value.slice(0, -1);
    }
    return value ? `${value}...` : "...";
  };

  const renderInfoLine = (y: number, label: string, value: string) => {
    doc.setFontSize(8);
    const labelText = `${label}: `;
    const valueText = fitTextToWidth(value, infoMaxValueWidth);
    const labelWidth = doc.getTextWidth(labelText);
    const valueWidth = doc.getTextWidth(valueText);
    const startX = infoRightX - (labelWidth + valueWidth);
    doc.setTextColor(...labelColor);
    doc.text(labelText, startX, y);
    doc.setTextColor(...valueColor);
    doc.text(valueText, startX + labelWidth, y);
  };

  const renderHeader = () => {
    if (config.logoDataUrl) {
      doc.addImage(config.logoDataUrl, "PNG", leftX, 20, logoWidth, logoHeight);
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text("Comparativo de Ventas", titleX, titleY);

    doc.setFontSize(8);
    doc.setTextColor(...labelColor);
    doc.text("Generado: ", titleX, titleY + 13);
    doc.setTextColor(...valueColor);
    doc.text(generatedAt, titleX + doc.getTextWidth("Generado: "), titleY + 13);

    doc.setTextColor(...labelColor);
    doc.text("Usuario: ", titleX, titleY + 24);
    doc.setTextColor(...valueColor);
    doc.text(userName, titleX + doc.getTextWidth("Usuario: "), titleY + 24);

    doc.setTextColor(...labelColor);
    doc.text("Servidor: ", titleX, titleY + 35);
    doc.setTextColor(...valueColor);
    doc.text(
      config.server || "Sin seleccionar",
      titleX + doc.getTextWidth("Servidor: "),
      titleY + 35,
    );

    infoLines.forEach((line, index) => {
      const y = infoStartY + index * infoRowHeight;
      renderInfoLine(y, line.label, line.value);
    });

    doc.setTextColor(0, 0, 0);
  };

  const head = [
    [
      "SUCURSAL",
      ...(config.showSubdivision ? ["SUBDIVISIÓN"] : []),
      `VENTAS ${config.anhoAnterior}`,
      `VENTAS ${config.anhoActual}`,
      "DIFERENCIA (GS)",
      "% DIFERENCIA",
      `TICKETS ${config.anhoAnterior}`,
      `TICKETS ${config.anhoActual}`,
      "DIF. TICKETS",
    ],
  ];

  const body = config.rows.map((row) => [
    row.sucursal,
    ...(config.showSubdivision ? [formatSubdivision(row.subdivision)] : []),
    formatCurrency(row.ventaAnterior),
    formatCurrency(row.ventaActual),
    formatCurrency(row.variacionAbs),
    formatVariationPct(row.variacionPct),
    formatNumber(row.ticketsAnterior),
    formatNumber(row.ticketsActual),
    formatNumber(row.ticketsVariacionAbs),
  ]);

  const totalRow = [
    {
      content: config.totalLabel,
      styles: TOTAL_ROW_CELL_STYLE,
    },
    ...(config.showSubdivision
      ? [
          {
            content: "-",
            styles: TOTAL_ROW_CELL_STYLE,
          },
        ]
      : []),
    {
      content: formatCurrency(config.total.ventaAnterior),
      styles: TOTAL_ROW_CELL_STYLE,
    },
    {
      content: formatCurrency(config.total.ventaActual),
      styles: TOTAL_ROW_CELL_STYLE,
    },
    {
      content: formatCurrency(config.total.variacionAbs),
      styles: TOTAL_ROW_CELL_STYLE,
    },
    {
      content: formatVariationPct(config.totalVariacionPct),
      styles: TOTAL_ROW_CELL_STYLE,
    },
    {
      content: formatNumber(config.total.ticketsAnterior),
      styles: TOTAL_ROW_CELL_STYLE,
    },
    {
      content: formatNumber(config.total.ticketsActual),
      styles: TOTAL_ROW_CELL_STYLE,
    },
    {
      content: formatNumber(config.total.ticketsVariacionAbs),
      styles: TOTAL_ROW_CELL_STYLE,
    },
  ];

  const columnStyles: Record<number, Partial<Styles>> = {
    0: { halign: "left" as HAlignType },
  };
  const numericStart = config.showSubdivision ? 2 : 1;
  if (config.showSubdivision) {
    columnStyles[1] = { halign: "left" as HAlignType };
  }
  for (let index = numericStart; index < head[0].length; index += 1) {
    columnStyles[index] = { halign: "right" as HAlignType };
  }

  autoTable(doc, {
    startY: headerHeight + 14,
    margin: { left: leftX, right: rightX, top: headerHeight + 14, bottom: 30 },
    head,
    body: [...body, totalRow],
    styles: {
      fontSize: 8,
      cellPadding: 4,
      lineColor: [214, 220, 229],
      lineWidth: 0.4,
      valign: "middle",
    },
    headStyles: {
      fillColor: [24, 32, 39],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    alternateRowStyles: {
      fillColor: [236, 236, 236],
    },
    columnStyles,
    didDrawPage: () => {
      renderHeader();
      const pageNumber = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(...valueColor);
      doc.text(
        `Página ${pageNumber}`,
        pageWidth - rightX,
        doc.internal.pageSize.getHeight() - 12,
        { align: "right" },
      );
      doc.setTextColor(0, 0, 0);
    },
  });

  doc.save(
    `comparativo-ventas-${tabSuffixMap[config.tab]}-${buildTimestamp()}.pdf`,
  );
};
